import { Box, useMediaQuery, useTheme } from "@mui/material";
import api from "app/api";
import {
  Autocomplete,
  Checkbox,
  DatePicker,
  File,
  HtmlEditor,
  HtmlView,
  ImageFile,
  RadioGroup,
  Signature,
  TextField,
  TimePicker,
} from "common";
import I18n from "i18n-js";
import { FieldCoordinates, useFormEditor } from "./FormEditorDataProvider";
import { FieldItem, FieldType } from "./types";
export interface InputProps {
  field: FieldItem;
  coordinates: FieldCoordinates;
  preview?: boolean;
  error?: any;
}

export default function Input({
  field,
  coordinates,
  preview,
  error,
}: InputProps) {
  const theme = useTheme();
  const { handleFieldChange } = useFormEditor();
  const { uploadFile } = api.getService("/files");
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  function onChange(evt: any) {
    let value = evt.target.value;

    if (field.type === FieldType.signature) {
      value = field.value;

      if (!value) {
        value = { signature: undefined, name: undefined };
      }

      if (evt.target?.id == field.name + "-name") {
        value.name = evt.target.value;
      } else {
        value.signature = evt.target.value;
      }
    }

    handleFieldChange(coordinates, { ...field, value });
  }

  let value: any = field.value;

  return (
    <>
      {[FieldType.text, FieldType.multiline_text].includes(field.type) && (
        <TextField
          id={field.name}
          label={field.name}
          mask={field.mask}
          value={value}
          minRows={field.rows}
          multiline={field.multiline}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.date && (
        <DatePicker
          id={field.name}
          label={field.name}
          value={value}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.time && (
        <TimePicker
          id={field.name}
          label={field.name}
          value={value}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.checkbox && (
        <Checkbox
          id={field.name}
          label={field.name}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.radio_group && (
        <RadioGroup
          id={field.name}
          label={field.name}
          //options={field.options?.map(o => ({ value: o, label: o }))}
          options={Object.values(field.options || {}).map(o => ({
            value: o,
            label: o,
          }))}
          value={value}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.select && (
        <Autocomplete
          id={field.name}
          label={field.name}
          //options={field.options?.map(o => ({ value: o, label: o }))}
          options={Object.values(field.options || {}).map(o => ({
            value: o,
            label: o,
          }))}
          multiple={field.multiple}
          value={value}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.paragraph && !preview && (
        <HtmlEditor
          id={field.name}
          label={field.name}
          value={value}
          //required={field.required}
          onChange={onChange}
          //placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
        />
      )}
      {field.type === FieldType.paragraph && preview && (
        <HtmlView id={field.name} value={value} />
      )}
      {field.type === FieldType.image && (
        <ImageFile
          id={field.name}
          value={value}
          upload={uploadFile}
          required={field.required}
          onChange={onChange}
          stackProps={{ sx: { position: "initial" } }}
          type="img"
          placeholder={field.placeholder || "Upload an image"}
          helperText={field.helperText}
          error={error}
          //uri={value?.url}
          useObjectValue
          readOnly={preview}
          showTolbar={!preview}
        />
      )}
      {field.type === FieldType.file && (
        <File
          id={field.name}
          value={value}
          upload={uploadFile}
          required={field.required}
          onChange={onChange}
          placeholder={field.placeholder}
          helperText={field.helperText}
          error={error}
          //uri={value?.url}
          useObjectValue
        />
      )}
      {field.type === FieldType.signature && (
        <Box
          sx={{
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            alignContent: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            id={field.name + "-name"}
            label={I18n.t("FormEditor.signatureFullName")}
            required={field.required}
            onChange={onChange}
            placeholder={I18n.t("FormEditor.signatureFullNamePlaceholder")}
            InputLabelProps={{ shrink: true }}
            style={{
              width: mdUp ? 400 : Math.round(400 * 0.75),
            }}
          />
          <Signature
            id={field.name}
            value={value}
            required={field.required}
            onChange={onChange}
            label={field.name}
            height={mdUp ? 200 : Math.round(200 * 0.75)}
            width={mdUp ? 400 : Math.round(400 * 0.75)}
            //placeholder={field.placeholder}
            helperText={field.helperText}
            error={error}
          />
        </Box>
      )}
    </>
  );
}
